package com.eladfinish.bible.contest.ui.components.bottomSheet

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.SheetState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch

/**
 * A reusable modal bottom sheet with:
 * - Optional sticky header title
 * - Close (X) button
 * - Animated close via [SheetState.hide]
 *
 * The [content] receives a `hideAnimated` callback so it can close the sheet from within
 * (e.g., after selecting an option).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ModalBottomSheetWithAnimatedClose(
    sheetState: SheetState,
    onHide: () -> Unit,
    title: String? = null,
    modifier: Modifier = Modifier,
    contentPadding: PaddingValues = PaddingValues(16.dp),
    content: @Composable (hideAnimated: () -> Unit) -> Unit,
) {
    val scope = rememberCoroutineScope()

    fun hideAnimated() {
        scope.launch {
            sheetState.hide()
            onHide()
        }
    }

    ModalBottomSheet(
        onDismissRequest = {
            // When dismissed by clicking outside or back button, just sync the state
            onHide()
        },
        sheetState = sheetState,
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding(),
        ) {
            // Sticky header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (title != null) {
                    Text(
                        text = title,
                        style = MaterialTheme.typography.titleMedium,
                        modifier = Modifier.weight(1f),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                } else {
                    Spacer(
                        modifier = Modifier.weight(1f),
                    )
                }

                IconButton(
                    onClick = { hideAnimated() },
                ) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = "Close",
                    )
                }
            }

            HorizontalDivider()

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(contentPadding),
            ) {
                content(::hideAnimated)
            }
        }
    }
}
